import { QRCodeSVG } from 'qrcode.react'
import type { CSSProperties } from 'react'
import { PRINT_LOGO_SRC, WORDMARK_SRC } from '~/features/events/checkin-flyer/flyer-content'
import { cn } from '~/shared/lib/utils'
import type { FlyerTone, LunchFlyerContent } from '../lib/place-flyer-content'

const DENSITY = {
  roomy: {
    day: 'text-[3.8cqw]',
    dish: 'text-[3.6cqw]',
    detail: 'text-[2.7cqw]',
    gap: 'gap-[3cqw]',
  },
  regular: {
    day: 'text-[3.2cqw]',
    dish: 'text-[3cqw]',
    detail: 'text-[2.3cqw]',
    gap: 'gap-[2cqw]',
  },
  compact: {
    day: 'text-[2.6cqw]',
    dish: 'text-[2.4cqw]',
    detail: 'text-[1.9cqw]',
    gap: 'gap-[1.2cqw]',
  },
} as const

export function LunchMenuFlyer({
  content,
  tone,
  className,
  style,
}: {
  content: LunchFlyerContent
  tone: FlyerTone
  className?: string
  style?: CSSProperties
}) {
  const color = tone === 'color'
  const size = DENSITY[content.density]
  return (
    <div
      style={style}
      className={cn(
        '@container flex flex-col overflow-hidden [-webkit-print-color-adjust:exact] [print-color-adjust:exact]',
        color ? 'bg-flyer-night text-flyer-paper' : 'bg-print-paper text-print-ink',
        className,
      )}
    >
      <div className="flex size-full flex-col px-[8cqw] pt-[8cqw] pb-[6cqw]">
        <img
          src={color ? WORDMARK_SRC : PRINT_LOGO_SRC}
          alt=""
          className="h-[4.5cqw] w-auto self-start"
        />
        <h1 className="mt-[5cqw] line-clamp-2 font-serif text-[9cqw] leading-[1.05] font-semibold">
          {content.title}
        </h1>
        <p className="mt-[1.6cqw] text-[3.2cqw] font-medium">
          {content.placeName} · {content.period}
        </p>
        <span
          className={cn(
            'mt-[3cqw] h-[0.8cqw] w-[10cqw] rounded-full',
            color ? 'bg-flyer-accent' : 'bg-print-ink',
          )}
        />
        <div className={cn('mt-[4cqw] flex min-h-0 flex-1 flex-col overflow-hidden', size.gap)}>
          {content.days.map((day) => (
            <section key={day.weekday} className="flex flex-col gap-[0.8cqw]">
              <h2
                className={cn(
                  'font-semibold tracking-[0.12em] uppercase',
                  size.day,
                  color && 'text-flyer-accent',
                )}
              >
                {day.label}
              </h2>
              {day.dishes.map((dish) => (
                <div key={dish.key} className="flex items-baseline gap-[2cqw]">
                  <div className="min-w-0 flex-1">
                    <p className={cn('leading-snug font-medium', size.dish)}>{dish.name}</p>
                    {(dish.description || dish.diets) && (
                      <p className={cn('leading-snug opacity-70', size.detail)}>
                        {[dish.description, dish.diets].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  {dish.price && (
                    <p className={cn('shrink-0 font-semibold tabular-nums', size.dish)}>
                      {dish.price}
                    </p>
                  )}
                </div>
              ))}
            </section>
          ))}
        </div>
        {content.note && (
          <p className="mt-[3cqw] line-clamp-2 text-[2.6cqw] leading-snug opacity-80">
            {content.note}
          </p>
        )}
        <div className="mt-[4cqw] flex items-center gap-[4cqw]">
          <div className="shrink-0 rounded-[2cqw] bg-print-paper p-[1.6cqw]">
            <QRCodeSVG value={content.url} marginSize={0} className="block size-[18cqw]" />
          </div>
          <p className="text-[2.8cqw] leading-snug font-medium">{content.scanHint}</p>
        </div>
      </div>
    </div>
  )
}
